/**
 * Centralized File Validation Utility
 *
 * Security-focused file validation for upload components.
 * Provides whitelist-based validation and blocks dangerous file types.
 *
 * @module fileValidation
 */

/**
 * Allowed file extensions by category
 * Use these to configure upload components based on their purpose
 */
export const ALLOWED_EXTENSIONS = {
  /** Safe document formats (no macro support) */
  documents: ['.pdf', '.doc', '.docx', '.txt', '.csv', '.rtf'],

  /** Safe spreadsheet formats (no macro support) */
  spreadsheets: ['.xlsx', '.xls', '.csv'],

  /** Safe image formats (no script execution) */
  images: ['.png', '.jpg', '.jpeg', '.gif', '.bmp', '.tiff', '.tif', '.webp'],

  /** Safe presentation formats (no macro support) */
  presentations: ['.ppt', '.pptx'],

  /** CAD and 3D model formats for Forge Viewer */
  cad: [
    '.dwg', '.dwf', '.dxf', '.dwfx',
    '.idw', '.ipt', '.iam',
    '.rvt', '.rfa',
    '.skp',
    '.ifc',
    '.stl', '.obj', '.fbx', '.3ds',
    '.iges', '.step', '.stp',
    '.sldprt', '.sldasm',
    '.nwd', '.nwc',
    '.sat',
    '.catpart', '.catproduct',
    '.par', '.asm', '.prt',
    '.neu',
    '.rhino', '.3dm',
    '.dgn'
  ],

  /** Data interchange formats */
  data: ['.json', '.xml', '.csv']
};

/**
 * Blocked file extensions - these are NEVER allowed regardless of context
 * These file types can execute code or contain malicious payloads
 */
export const BLOCKED_EXTENSIONS = [
  // Executable files
  '.exe', '.bat', '.cmd', '.com', '.msi', '.ps1', '.vbs', '.vbe',
  '.jar', '.sh', '.bash', '.zsh', '.ksh', '.csh',
  '.app', '.deb', '.rpm', '.dmg', '.pkg',
  '.scr', '.pif', '.gadget', '.msc', '.msp',

  // Macro-enabled Office documents
  '.xlsm', '.xltm', '.xlam', '.xla',  // Excel with macros
  '.docm', '.dotm',                     // Word with macros
  '.pptm', '.potm', '.ppam', '.ppsm',   // PowerPoint with macros
  '.sldm',                              // SharePoint with macros

  // Script files
  '.js', '.jse', '.ts', '.mjs', '.cjs',
  '.php', '.php3', '.php4', '.php5', '.phtml',
  '.asp', '.aspx', '.ascx', '.ashx', '.asmx',
  '.jsp', '.jspx',
  '.py', '.pyc', '.pyo', '.pyw',
  '.pl', '.pm', '.cgi',
  '.rb', '.erb',
  '.lua',
  '.wsf', '.wsh', '.ws',

  // System/library files
  '.dll', '.so', '.dylib', '.sys', '.drv',
  '.ocx', '.cpl', '.inf', '.reg',

  // Dangerous media/markup (can contain scripts)
  '.svg', '.svgz',           // SVG can contain JavaScript
  '.html', '.htm', '.xhtml', // HTML can contain scripts
  '.hta',                    // HTML Application (runs with full trust)
  '.swf', '.fla',            // Flash (deprecated, security risk)
  '.xap',                    // Silverlight (deprecated)

  // Archive types that might contain executables
  '.iso', '.img',            // Disk images
  '.msi', '.msix', '.appx',  // Windows installers

  // Other dangerous formats
  '.lnk', '.url',            // Shortcuts (can redirect to malware)
  '.scf',                    // Shell Command File
  '.prf'                     // Outlook profile (can be malicious)
];

/**
 * MIME types that are blocked
 */
export const BLOCKED_MIME_TYPES = [
  'application/x-msdownload',
  'application/x-executable',
  'application/x-msdos-program',
  'application/x-sh',
  'application/x-shellscript',
  'application/javascript',
  'text/javascript',
  'application/x-javascript',
  'text/html',
  'application/xhtml+xml',
  'image/svg+xml',
  'application/x-shockwave-flash'
];

/**
 * Safe MIME types for images
 */
export const SAFE_IMAGE_MIME_TYPES = [
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/bmp',
  'image/tiff',
  'image/webp'
];

/**
 * Safe MIME types for documents
 */
export const SAFE_DOCUMENT_MIME_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'text/plain',
  'text/csv'
];

/**
 * Get file extension from filename (lowercase, with dot)
 * @param {string} filename - The filename to extract extension from
 * @returns {string} The file extension (e.g., '.xlsx') or empty string
 */
export function getFileExtension(filename) {
  if (!filename || typeof filename !== 'string') {
    return '';
  }
  const lastDot = filename.lastIndexOf('.');
  if (lastDot === -1 || lastDot === filename.length - 1) {
    return '';
  }
  return filename.slice(lastDot).toLowerCase();
}

/**
 * Check if a file extension is in the blocked list
 * @param {string} filename - The filename to check
 * @returns {boolean} True if the extension is blocked
 */
export function isBlockedExtension(filename) {
  const ext = getFileExtension(filename);
  return BLOCKED_EXTENSIONS.includes(ext);
}

/**
 * Check if a MIME type is blocked
 * @param {string} mimeType - The MIME type to check
 * @returns {boolean} True if the MIME type is blocked
 */
export function isBlockedMimeType(mimeType) {
  if (!mimeType) return false;
  return BLOCKED_MIME_TYPES.some(blocked =>
    mimeType.toLowerCase().includes(blocked.toLowerCase())
  );
}

/**
 * Validate a file extension against an allowed list
 * @param {string} filename - The filename to validate
 * @param {string[]} allowedExtensions - Array of allowed extensions (e.g., ['.xlsx', '.csv'])
 * @returns {boolean} True if the extension is allowed
 */
export function validateFileExtension(filename, allowedExtensions) {
  if (!filename || !Array.isArray(allowedExtensions)) {
    return false;
  }

  // First check if it's in the blocked list
  if (isBlockedExtension(filename)) {
    return false;
  }

  const ext = getFileExtension(filename);
  return allowedExtensions.some(allowed =>
    allowed.toLowerCase() === ext
  );
}

/**
 * Validate a file's MIME type
 * @param {File} file - The File object to validate
 * @param {string[]} allowedMimeTypes - Array of allowed MIME types
 * @returns {boolean} True if the MIME type is allowed
 */
export function validateFileMimeType(file, allowedMimeTypes) {
  if (!file || !file.type || !Array.isArray(allowedMimeTypes)) {
    return false;
  }

  // Check if blocked
  if (isBlockedMimeType(file.type)) {
    return false;
  }

  return allowedMimeTypes.some(allowed =>
    file.type.toLowerCase() === allowed.toLowerCase()
  );
}

/**
 * Comprehensive file validation
 * Validates both extension and MIME type, and checks against blocklist
 * @param {File} file - The File object to validate
 * @param {Object} options - Validation options
 * @param {string[]} [options.allowedExtensions] - Allowed file extensions
 * @param {string[]} [options.allowedMimeTypes] - Allowed MIME types
 * @param {number} [options.maxSizeBytes] - Maximum file size in bytes
 * @returns {{valid: boolean, error?: string}} Validation result
 */
export function validateFile(file, options = {}) {
  const { allowedExtensions, allowedMimeTypes, maxSizeBytes } = options;

  if (!file) {
    return { valid: false, error: 'No file provided' };
  }

  const filename = file.name || '';

  // Check blocklist first
  if (isBlockedExtension(filename)) {
    const ext = getFileExtension(filename);
    return {
      valid: false,
      error: `File type "${ext}" is not allowed for security reasons`
    };
  }

  if (file.type && isBlockedMimeType(file.type)) {
    return {
      valid: false,
      error: `File type "${file.type}" is not allowed for security reasons`
    };
  }

  // Check extension whitelist
  if (allowedExtensions && allowedExtensions.length > 0) {
    const ext = getFileExtension(filename);
    const isAllowed = allowedExtensions.some(allowed =>
      allowed.toLowerCase() === ext
    );
    if (!isAllowed) {
      return {
        valid: false,
        error: `File type "${ext}" is not supported. Allowed types: ${allowedExtensions.join(', ')}`
      };
    }
  }

  // Check MIME type whitelist
  if (allowedMimeTypes && allowedMimeTypes.length > 0 && file.type) {
    const isAllowed = allowedMimeTypes.some(allowed =>
      file.type.toLowerCase() === allowed.toLowerCase()
    );
    if (!isAllowed) {
      return {
        valid: false,
        error: `File format "${file.type}" is not supported`
      };
    }
  }

  // Check file size
  if (maxSizeBytes && file.size > maxSizeBytes) {
    const maxMB = (maxSizeBytes / (1024 * 1024)).toFixed(1);
    const fileMB = (file.size / (1024 * 1024)).toFixed(1);
    return {
      valid: false,
      error: `File size (${fileMB}MB) exceeds maximum allowed (${maxMB}MB)`
    };
  }

  return { valid: true };
}

/**
 * Validate multiple files
 * @param {FileList|File[]} files - Files to validate
 * @param {Object} options - Validation options (same as validateFile)
 * @returns {{valid: boolean, errors: Array<{file: string, error: string}>}} Validation result
 */
export function validateFiles(files, options = {}) {
  const errors = [];

  for (const file of files) {
    const result = validateFile(file, options);
    if (!result.valid) {
      errors.push({
        file: file.name,
        error: result.error
      });
    }
  }

  return {
    valid: errors.length === 0,
    errors
  };
}

/**
 * Get a human-readable list of allowed extensions for UI display
 * @param {string[]} extensions - Array of extensions
 * @returns {string} Formatted string (e.g., "XLSX, XLS, CSV")
 */
export function formatAllowedExtensions(extensions) {
  return extensions
    .map(ext => ext.replace('.', '').toUpperCase())
    .join(', ');
}

/**
 * Build an accept attribute value for file inputs
 * @param {string[]} extensions - Array of extensions to accept
 * @param {string[]} [mimeTypes] - Optional MIME types to include
 * @returns {string} Value for the accept attribute
 */
export function buildAcceptAttribute(extensions, mimeTypes = []) {
  const parts = [...extensions];
  if (mimeTypes.length > 0) {
    parts.push(...mimeTypes);
  }
  return parts.join(',');
}

// Pre-built accept strings for common use cases
export const ACCEPT_STRINGS = {
  /** Safe spreadsheet formats only */
  spreadsheets: '.xlsx,.xls,.csv',

  /** Safe image formats only (no SVG) */
  images: '.png,.jpg,.jpeg,.gif,.bmp,.tiff,.webp',

  /** Safe document formats */
  documents: '.pdf,.doc,.docx,.txt,.csv,.rtf',

  /** Combined office documents (safe only) */
  office: '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv',

  /** All safe attachment types */
  attachments: '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.png,.jpg,.jpeg,.gif,.bmp'
};

export default {
  ALLOWED_EXTENSIONS,
  BLOCKED_EXTENSIONS,
  BLOCKED_MIME_TYPES,
  SAFE_IMAGE_MIME_TYPES,
  SAFE_DOCUMENT_MIME_TYPES,
  ACCEPT_STRINGS,
  getFileExtension,
  isBlockedExtension,
  isBlockedMimeType,
  validateFileExtension,
  validateFileMimeType,
  validateFile,
  validateFiles,
  formatAllowedExtensions,
  buildAcceptAttribute
};
